import DOMPurify from "dompurify";

// Register sanitization hooks exactly once at module import time:
//   1. <img> tags get loading=lazy + decoding=async to defer off-screen
//      downloads and keep article render non-blocking.
//   2. <a target="_blank"> gets rel="noopener noreferrer" merged in so the
//      linked page can never reach window.opener (tabnabbing) or leak the
//      Referer. CMS authors can't be trusted to add this themselves, and
//      DOMPurify's defaults preserve whatever rel was on the source.
//   3. The `sabo-article` class is dropped from content. components/KbArticle
//      supplies the single styled root; a wrapper authored into the article
//      would nest a second root that re-declares the palette and density
//      variables inside it.
let hooksRegistered = false;
function ensureHooks(): void {
  if (hooksRegistered) return;
  hooksRegistered = true;
  DOMPurify.addHook("uponSanitizeElement", (node) => {
    if (!(node instanceof Element)) return;

    if (node.classList.contains("sabo-article")) {
      node.classList.remove("sabo-article");
      if (!node.classList.length) node.removeAttribute("class");
    }

    if (node.nodeName === "IMG") {
      if (!node.getAttribute("loading")) {
        node.setAttribute("loading", "lazy");
      }
      if (!node.getAttribute("decoding")) {
        node.setAttribute("decoding", "async");
      }
      return;
    }

    if (node.nodeName === "A" && node.getAttribute("target") === "_blank") {
      const existingRel = node.getAttribute("rel") ?? "";
      const tokens = new Set(existingRel.split(/\s+/).filter(Boolean));
      tokens.add("noopener");
      tokens.add("noreferrer");
      node.setAttribute("rel", Array.from(tokens).join(" "));
    }
  });
}

// Allow-list drawn from what the KB editor typically produces: basic block
// and inline formatting, links, headings, lists, images, and code snippets.
// Scripts, iframes, event handlers, and any `javascript:` URLs are stripped
// by DOMPurify's defaults.
const ALLOWED_TAGS: string[] = [
  "a",
  "b",
  "blockquote",
  "br",
  "code",
  "div",
  "em",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "i",
  "img",
  "li",
  "ol",
  "p",
  "pre",
  "section",
  "span",
  "strong",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
];

const ALLOWED_ATTR: string[] = [
  "href",
  "title",
  "target",
  "rel",
  "src",
  "alt",
  "width",
  "height",
  "loading",
  "decoding",
  "class",
];

export function sanitizeArticleHtml(html: string): string {
  ensureHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
  });
}
